import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Dices, Copy, Download, RefreshCw, AlertTriangle, X, Wand2, Check,
  Zap, ListX, ArrowLeftRight, Lightbulb, Camera, Users, GraduationCap, Eye,
  Clapperboard, Repeat, Ban, UtensilsCrossed, MapPin, ClipboardList, ChefHat, Euro, Smile,
  Package, MessageSquareText,
} from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import { AddToCalendar } from "../components/AddToCalendar";
import api, { formatApiErrorDetail } from "../lib/api";

const FORMATS = [
  { id: "payoff", nome: "Payoff First (Quick-Cut)", tag: "high completion", resto: false },
  { id: "errori", nome: "3 Mistakes", tag: "high saves", resto: false },
  { id: "primadopo", nome: "Before → After", tag: "transformation", resto: false, u: true },
  { id: "mito", nome: "Myth-busting", tag: "high comments", resto: false },
  { id: "backstage", nome: "Behind the scenes", tag: "trust", resto: false, u: true },
  { id: "casestudy", nome: "Customer story", tag: "conversion", resto: false, u: true },
  { id: "tutorial", nome: "Quick tutorial", tag: "saves + follows", resto: false },
  { id: "pov", nome: "Immersive POV", tag: "relatability", resto: false, u: true },
  { id: "serie", nome: "Series episode", tag: "recurring followers", resto: false, u: true },
  { id: "loop", nome: "Visual loop", tag: "re-watch", resto: false, u: true },
  { id: "nonfare", nome: '"Don\'t do this"', tag: "contrarian", resto: false },
  { id: "foodreveal", nome: "Food Reveal (ASMR)", tag: "craving trigger", resto: true },
  { id: "localpov", nome: "The hidden spot in…", tag: "local discovery", resto: true },
  { id: "cosaordinare", nome: "What to order at…", tag: "local saves", resto: true },
  { id: "chefstory", nome: "The owner's story", tag: "trust", resto: true },
  { id: "prezzochallenge", nome: "What you get for X€", tag: "transparency", resto: true },
  { id: "reaction", nome: "The first reaction", tag: "social proof", resto: true },
];
const formatsForMode = (mode) => (mode === "resto" ? FORMATS.filter((f) => f.resto || f.u) : FORMATS.filter((f) => !f.resto));

const FORMAT_ICONS = {
  payoff: Zap, errori: ListX, primadopo: ArrowLeftRight, mito: Lightbulb, backstage: Camera,
  casestudy: Users, tutorial: GraduationCap, pov: Eye, serie: Clapperboard, loop: Repeat,
  nonfare: Ban, foodreveal: UtensilsCrossed, localpov: MapPin, cosaordinare: ClipboardList,
  chefstory: ChefHat, prezzochallenge: Euro, reaction: Smile,
};

const VENUES = [
  ["pizzeria", "Pizzeria"], ["trattoria", "Trattoria / osteria"], ["classico", "Ristorante"],
  ["finedining", "Fine dining"], ["sushi", "Sushi / ethnic"], ["street", "Street food"],
  ["brunch", "Bar / brunch / pastry"], ["ayce", "All you can eat"], ["rooftop", "Rooftop / lounge"], ["pub", "Pub / grill"],
];
const MOMENTI = ["cene e weekend", "cena di coppia", "pranzo di lavoro", "famiglie e gruppi", "brunch e colazioni", "aperitivo e serata", "delivery e asporto"];
const MOMENTO_LABEL = {
  "cene e weekend": "Dinners & weekends", "cena di coppia": "Date night", "pranzo di lavoro": "Business lunch",
  "famiglie e gruppi": "Families & groups", "brunch e colazioni": "Brunch & breakfast", "aperitivo e serata": "Aperitivo & nightlife",
  "delivery e asporto": "Delivery & takeaway",
};
const OBIETTIVI = ["vendite", "lead", "follower", "awareness"];
const OBIETTIVO_LABEL = { vendite: "Sales", lead: "Leads", follower: "Followers", awareness: "Awareness" };
const DURATE = [[25, "15–30 sec"], [45, "30–60 sec"], [75, "60–90 sec"]];
const LANGS = ["auto", "italiano", "inglese", "tedesco", "francese", "spagnolo", "portoghese", "olandese", "polacco", "greco", "svedese", "danese", "norvegese", "finlandese"];
const LANG_LABEL = {
  italiano: "Italian", inglese: "English", tedesco: "German", francese: "French", spagnolo: "Spanish",
  portoghese: "Portuguese", olandese: "Dutch", polacco: "Polish", greco: "Greek", svedese: "Swedish",
  danese: "Danish", norvegese: "Norwegian", finlandese: "Finnish",
};
const GOAL_MAP = { sales: "vendite", leads: "lead", awareness: "awareness" };
const TONE_LABEL = {
  friendly_warm: "Friendly & warm", bold_punchy: "Bold & punchy", expert_trustworthy: "Expert & trustworthy",
  playful_witty: "Playful & witty", premium_minimal: "Premium & minimal", down_to_earth: "Down-to-earth",
};
const MODE_LABEL = { prodotto: "Product", topic: "Topic", resto: "Restaurant" };

const buildExport = (r, out) => {
  const beats = out.beats.map((b) => `[${b.tempo}] ${b.label.toUpperCase()}\nVOICEOVER: ${b.voiceover}\nON CAMERA: ${b.azione}\nON-SCREEN: ${b.onscreen}`).join("\n\n");
  return `VIDEO SCRIPT — ${(r.fields.cliente || "").toUpperCase()}\nFormat: ${out.format} · Goal: ${OBIETTIVO_LABEL[r.fields.obiettivo] || r.fields.obiettivo} · Duration: ~${r.fields.durata}s\n──────────────────────────────────────────────\n\nSTRATEGY: ${out.strategia}\n\nHOOK (first 3 seconds):\n"${out.hook}"\n\n${beats}\n\nCTA: ${out.cta}\n\nCAPTION:\n${out.caption}\n\nDIRECTION NOTES:\n${out.regia.map((n) => `• ${n}`).join("\n")}`;
};

const copyText = (t) => navigator.clipboard.writeText(t).then(() => toast("Copied."));

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">{label}</label>
    {children}
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">{children}</p>
);

const BriefRow = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-3 py-2 border-b border-[#F3EFE7] last:border-0" data-testid={testId}>
    <span className="text-[11.5px] font-semibold text-[#9A9591] shrink-0">{label}</span>
    <span className="text-[12.5px] font-bold text-[#181621] text-right truncate tabular-nums">{value}</span>
  </div>
);

export default function ScriptLab() {
  const navigate = useNavigate();
  const location = useLocation();
  const [brain, setBrain] = useState(null);
  const tab = "script";
  const [phase, setPhase] = useState("idle"); // idle | generating | error
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState(null); // history doc
  const [history, setHistory] = useState([]);
  const [socialNotice, setSocialNotice] = useState(false);
  const [mode, setMode] = useState("prodotto");
  const [f, setF] = useState(() => {
    const defaults = { cliente: "", prodotto: "", target: "", obiettivo: "vendite", durata: 25, note: "", punti: "", cta_custom: "", format_id: "random", tipo_locale: "classico", citta: "", prezzo: "", hero: "", unico: "", momento: "cene e weekend" };
    try {
      const draft = JSON.parse(sessionStorage.getItem("agz_scriptlab_draft") || "null");
      return draft ? { ...defaults, ...draft } : defaults;
    } catch {
      return defaults;
    }
  });
  const [language, setLanguage] = useState("auto");
  const [refine, setRefine] = useState("");
  const [restoSetup, setRestoSetup] = useState({ citta: "", tipo: "classico" });
  const [calAdded, setCalAdded] = useState({}); // history_id -> true (real calendar state)
  const [calBusy, setCalBusy] = useState(false);
  const [references, setReferences] = useState([]);
  const [referenceId, setReferenceId] = useState("");
  const pollRef = useRef(null);
  const lastReq = useRef(null);
  const pendingSlotRef = useRef(null);
  const prefillDoneRef = useRef(false);

  const loadCalendarState = () =>
    api.get("/calendar").then(({ data }) => {
      const m = {};
      (data.slots || []).forEach((s) => { if (s.history_id) m[s.history_id] = true; });
      setCalAdded(m);
    }).catch(() => {});
  useEffect(() => {
    api.get("/references").then(({ data }) => setReferences(data.filter((r) => r.breakdown))).catch(() => {});
  }, []);

  const isFood = brain && /restaurant|food/i.test(brain.business_type || "");
  const needsRestoSetup = isFood && brain && !brain.resto_city;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/brand-brain").then(({ data }) => {
      setBrain(data);
      if (data) {
        const food = /restaurant|food/i.test(data.business_type || "");
        setMode(food ? "resto" : "prodotto");
        setF((p) => ({
          ...p,
          cliente: p.cliente || data.business_name || "",
          target: p.target || `${data.business_type} — ${data.market_scope} audience, ${data.country}`,
          obiettivo: p.obiettivo !== "vendite" ? p.obiettivo : (GOAL_MAP[data.primary_goal] || "vendite"),
          note: p.note || (TONE_LABEL[data.tone_of_voice] ? `Brand tone of voice: ${TONE_LABEL[data.tone_of_voice]}` : ""),
          citta: p.citta || data.resto_city || "",
          tipo_locale: p.tipo_locale !== "classico" ? p.tipo_locale : (data.resto_venue_type || "classico"),
        }));
      }
    }).catch(() => {});
    api.get("/strategy").then(({ data }) => {
      if (data) {
        const hasSocial = data.scenarios.some((s) => s.channels.some((c) => c.type === "paid_social" || /organic social|tiktok|meta/i.test(c.name)));
        setSocialNotice(!hasSocial);
      }
    }).catch(() => {});
    api.get("/script-lab/history").then(({ data }) => setHistory(data)).catch(() => {});
    loadCalendarState();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem("agz_scriptlab_draft", JSON.stringify(f));
    } catch { /* storage full or unavailable */ }
  }, [f]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      if (++ticks > 200) { clearInterval(pollRef.current); setPhase("error"); setErrMsg("Generation timed out. Try again."); return; }
      try {
        const { data } = await api.get("/script-lab/status");
        if (data.status === "failed") {
          clearInterval(pollRef.current);
          setPhase("error");
          setErrMsg(data.error || "Something broke on our side — try again.");
        } else if (data.status === "complete") {
          clearInterval(pollRef.current);
          const { data: h } = await api.get("/script-lab/history");
          setHistory(h);
          const latest = h[0];
          setResult(latest);
          if (pendingSlotRef.current) {
            await api.patch(`/calendar/slots/${pendingSlotRef.current}`, { status: "script_ready", history_id: latest.id }).catch(() => {});
            pendingSlotRef.current = null;
            loadCalendarState();
          }
          setPhase("idle");
          toast("Ready.");
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const generate = async (kind, overrides = {}) => {
    const fields = { ...f, ...(overrides.fields || {}) };
    const body = { mode: overrides.mode || mode, language, fields, ...(referenceId ? { reference_id: referenceId } : {}), ...(overrides.refine || {}) };
    lastReq.current = { kind, body };
    setPhase("generating");
    setErrMsg("");
    try {
      await api.post("/script-lab/generate", body);
      startPolling();
    } catch (err) {
      setPhase("error");
      setErrMsg(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const turnIntoScript = (idea, slotId = null) => {
    pendingSlotRef.current = slotId;
    if (mode === "resto") {
      const fields = { punti: `Idea del video: ${idea.titolo} — da toccare: ${idea.punti}`, cta_custom: idea.cta };
      setF((p) => ({ ...p, ...fields }));
      generate("script", { fields });
    } else {
      const newMode = idea.tipo === "prodotto" ? "prodotto" : "topic";
      const fields = { prodotto: idea.titolo + (f.prodotto ? ` — contesto: ${f.prodotto}` : ""), punti: idea.punti, cta_custom: idea.cta };
      setMode(newMode);
      setF((p) => ({ ...p, ...fields }));
      generate("script", { mode: newMode, fields });
    }
  };

  useEffect(() => {
    const rid = location.state?.referencePrefill;
    if (rid) {
      setReferenceId(rid);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const p = location.state?.scriptPrefill;
    if (p && brain && !prefillDoneRef.current) {
      prefillDoneRef.current = true;
      turnIntoScript(p.idea, p.slotId || null);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brain, location.state]);

  const addScriptToCalendar = async (date) => {
    setCalBusy(true);
    try {
      const req = result.request;
      const { data: slot } = await api.post("/calendar/slots", {
        title: result.output.hook || req.fields?.prodotto || "Script", date,
        tipo: req.mode === "topic" ? "topic" : "prodotto",
        punti: req.fields?.punti || "", cta: result.output.cta || "",
        status: "script_ready", history_id: result.id,
      });
      setCalAdded((m) => ({ ...m, [result.id]: true }));
      toast("Added to calendar.", { description: "Writing the caption…" });
      try {
        await api.post(`/calendar/slots/${slot.id}/caption`);
        toast("Caption ready.", { description: "Find it on the calendar slot — editable and copy-ready." });
      } catch {
        toast.error("Added, but the caption failed — write it from the calendar slot.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add to calendar.");
      loadCalendarState();
    } finally {
      setCalBusy(false);
    }
  };

  const saveRestoSetup = async () => {
    if (!restoSetup.citta.trim()) return toast.error("City is required.");
    await api.put("/brand-brain/extras", { resto_city: restoSetup.citta.trim(), resto_venue_type: restoSetup.tipo });
    setBrain((b) => ({ ...b, resto_city: restoSetup.citta.trim(), resto_venue_type: restoSetup.tipo }));
    setF((p) => ({ ...p, citta: restoSetup.citta.trim(), tipo_locale: restoSetup.tipo }));
  };

  if (brain === null) return null;

  const fmts = formatsForMode(mode);
  const out = result?.output;
  const selectedFmt = FORMATS.find((x) => x.id === f.format_id);
  const durataLabel = (DURATE.find(([v]) => v === f.durata) || [0, `${f.durata}s`])[1];

  return (
    <div className="p-6 sm:p-10 max-w-6xl" data-testid="script-lab-page">
      <MonoLabel>Organic Studio · Scripts</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Videos that stop the scroll
      </h1>
      <p className="text-[13.5px] text-[#54505B] mt-3 max-w-xl">
        Brief it once — get a shot-by-shot script with hook, beats, CTA and caption, built on a proven format.
      </p>

      {socialNotice && (
        <div className="card-soft mt-6 p-4 flex items-start justify-between gap-3 border-l-4 border-l-[#0B67FF]" data-testid="social-coherence-notice">
          <p className="text-[13px] text-[#54505B]"><span className="font-bold text-[#181621]">Heads up:</span> your current plan doesn't prioritize social video — these scripts are extra firepower.</p>
          <button onClick={() => setSocialNotice(false)} className="text-[#9A9591] hover:text-[#181621]" data-testid="dismiss-social-notice">
            <X size={16} strokeWidth={1.8} />
          </button>
        </div>
      )}

      {needsRestoSetup ? (
        <div className="card-soft mt-8 p-7 max-w-md" data-testid="resto-setup-card">
          <MonoLabel>ONE-TIME SETUP</MonoLabel>
          <p className="mt-4 font-bold text-[15px]">Where's the restaurant?</p>
          <p className="text-[12.5px] text-[#54505B] mt-1">Asked once — saved to your Brand Brain.</p>
          <div className="mt-5 space-y-4">
            <Field label="City & area">
              <input className="field-input" placeholder="e.g. Milano, Navigli" value={restoSetup.citta} onChange={(e) => setRestoSetup((p) => ({ ...p, citta: e.target.value }))} data-testid="resto-city-input" />
            </Field>
            <Field label="Venue type">
              <select className="field-input" value={restoSetup.tipo} onChange={(e) => setRestoSetup((p) => ({ ...p, tipo: e.target.value }))} data-testid="resto-venue-select">
                {VENUES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <CtaButton className="w-full" onClick={saveRestoSetup} data-testid="resto-setup-save-btn">Save & continue</CtaButton>
          </div>
        </div>
      ) : (
        <>
          <div className="grid lg:grid-cols-[1fr_290px] gap-6 mt-8 items-start">
            <div className="min-w-0">
              {!isFood && (
                <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid="mode-selector">
                  {[["prodotto", "Product video", "Sells a specific product or service"], ["topic", "Topic video", "Positions the brand on a theme"]].map(([m, l, d]) => (
                    <button
                      key={m}
                      onClick={() => { setMode(m); set("format_id", "random"); }}
                      className={`rounded-xl border p-4 text-left transition-colors flex items-start gap-3 ${mode === m ? "border-[#181621] bg-white shadow-sm" : "border-[#EAE5DB] bg-white/60 hover:border-[#9A9591]"}`}
                      data-testid={`mode-${m}`}
                    >
                      <span className={`w-5 h-5 mt-0.5 rounded-full border flex items-center justify-center shrink-0 ${mode === m ? "bg-[#181621] border-[#181621]" : "border-[#E7E2D6] bg-white"}`}>
                        {mode === m && <Check size={11} strokeWidth={3} className="text-white" />}
                      </span>
                      <span>
                        <span className="font-bold text-[13.5px] text-[#181621] flex items-center gap-1.5">
                          {m === "prodotto" ? <Package size={13} strokeWidth={1.8} /> : <MessageSquareText size={13} strokeWidth={1.8} />} {l}
                        </span>
                        <span className="block text-[11.5px] text-[#54505B] mt-0.5">{d}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <div className="card-soft p-6 sm:p-7" data-testid="brief-form">
                <SectionLabel>Brief</SectionLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <Field label="Business / brand"><input className="field-input" value={f.cliente} onChange={(e) => set("cliente", e.target.value)} placeholder="Business name" data-testid="field-cliente" /></Field>
                  <Field label="Who should watch it"><input className="field-input" value={f.target} onChange={(e) => set("target", e.target.value)} data-testid="field-target" /></Field>
                  {mode !== "resto" ? (
                    <div className="sm:col-span-2">
                      <Field label={mode === "topic" ? "Topic of THIS video *" : "Product / service of THIS video *"}>
                        <textarea className="field-input min-h-[70px]" value={f.prodotto} onChange={(e) => set("prodotto", e.target.value)} data-testid="field-prodotto" />
                      </Field>
                    </div>
                  ) : (
                    <>
                      <Field label="Hero dish / experience *"><input className="field-input" value={f.hero} onChange={(e) => set("hero", e.target.value)} data-testid="field-hero" /></Field>
                      <Field label="What makes it unique"><input className="field-input" value={f.unico} onChange={(e) => set("unico", e.target.value)} data-testid="field-unico" /></Field>
                      <Field label="Reference price"><input className="field-input" value={f.prezzo} onChange={(e) => set("prezzo", e.target.value)} placeholder="optional" data-testid="field-prezzo" /></Field>
                      <Field label="Occasion / moment">
                        <select className="field-input" value={f.momento} onChange={(e) => set("momento", e.target.value)} data-testid="field-momento">
                          {MOMENTI.map((m) => <option key={m} value={m}>{MOMENTO_LABEL[m] || m}</option>)}
                        </select>
                      </Field>
                    </>
                  )}
                  {tab === "script" && (
                    <>
                      <Field label="Must-cover points"><input className="field-input" value={f.punti} onChange={(e) => set("punti", e.target.value)} placeholder="optional" data-testid="field-punti" /></Field>
                      <Field label="Final CTA / message"><input className="field-input" value={f.cta_custom} onChange={(e) => set("cta_custom", e.target.value)} placeholder="optional" data-testid="field-cta-custom" /></Field>
                    </>
                  )}
                </div>

                <div className="mt-7 pt-6 border-t border-[#F3EFE7]">
                  <SectionLabel>Setup</SectionLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <Field label="Goal">
                      <select className="field-input" value={f.obiettivo} onChange={(e) => set("obiettivo", e.target.value)} data-testid="field-obiettivo">
                        {OBIETTIVI.map((o) => <option key={o} value={o}>{OBIETTIVO_LABEL[o] || o}</option>)}
                      </select>
                    </Field>
                    <Field label="Duration">
                      <select className="field-input" value={f.durata} onChange={(e) => set("durata", Number(e.target.value))} data-testid="field-durata">
                        {DURATE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </Field>
                    <Field label="Script language">
                      <select className="field-input" value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="field-language">
                        {LANGS.map((l) => <option key={l} value={l}>{l === "auto" ? `Auto — ${brain?.country || "country"} language` : (LANG_LABEL[l] || l)}</option>)}
                      </select>
                    </Field>
                    <Field label="Notes"><input className="field-input" value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="field-note" /></Field>
                  </div>
                </div>
              </div>

              {tab === "script" && (
                <div className="card-soft p-6 sm:p-7 mt-5">
                  <SectionLabel>Format</SectionLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mt-4" data-testid="format-picker">
                    <button
                      onClick={() => set("format_id", "random")}
                      title="Let the engine pick the best format"
                      className={`rounded-xl border p-3 text-left transition-colors ${f.format_id === "random" ? "border-[#181621] bg-white shadow-sm" : "border-[#E0603A]/40 bg-[#FDF1EC] hover:border-[#E0603A]"}`}
                      data-testid="format-random"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-[#E0603A] flex items-center justify-center shrink-0">
                          <Dices size={14} strokeWidth={2} className="text-white" />
                        </span>
                        <span className="font-bold text-[12.5px] text-[#181621] leading-tight">Pick for me</span>
                      </span>
                      <span className="block text-[10.5px] font-semibold text-[#B4552D] mt-1.5">Recommended — the engine chooses</span>
                    </button>
                    {fmts.map((fmt) => {
                      const Icon = FORMAT_ICONS[fmt.id] || Clapperboard;
                      const sel = f.format_id === fmt.id;
                      return (
                        <button
                          key={fmt.id}
                          onClick={() => set("format_id", fmt.id)}
                          title={fmt.tag}
                          className={`rounded-xl border p-3 text-left transition-colors ${sel ? "border-[#181621] bg-white shadow-sm" : "border-[#EAE5DB] bg-white/60 hover:border-[#9A9591]"}`}
                          data-testid={`format-${fmt.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${sel ? "bg-[#181621]" : "bg-[#F3EFE7]"}`}>
                              <Icon size={14} strokeWidth={1.8} className={sel ? "text-white" : "text-[#54505B]"} />
                            </span>
                            <span className="font-bold text-[12.5px] text-[#181621] leading-tight">{fmt.nome}</span>
                          </span>
                          <span className="block text-[10.5px] text-[#9A9591] mt-1.5">{fmt.tag}</span>
                        </button>
                      );
                    })}
                  </div>
                  {references.length > 0 && (
                    <div className="mt-6 pt-5 border-t border-[#F3EFE7]">
                      <label className="text-[11px] font-semibold text-[#9A9591] block mb-2">Use a reference — structure only, never the content</label>
                      <select
                        className="field-input !py-2.5 max-w-md"
                        value={referenceId}
                        onChange={(e) => setReferenceId(e.target.value)}
                        data-testid="reference-selector"
                      >
                        <option value="">No reference</option>
                        {references.map((r) => (
                          <option key={r.id} value={r.id}>
                            {(r.breakdown?.hook_quote || r.page_name || r.url || "Reference").slice(0, 60)}{r.breakdown?.format_name ? ` · ${r.breakdown.format_name}` : ""}
                          </option>
                        ))}
                      </select>
                      {referenceId && (
                        <p className="text-[11.5px] text-[#96691E] mt-1.5" data-testid="reference-hint">
                          The script will replicate this reference's beats and rhythm, adapted to your business.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <aside className="card-soft p-5 lg:sticky lg:top-6" data-testid="brief-summary-panel">
              <SectionLabel>Your brief</SectionLabel>
              <div className="mt-3">
                <BriefRow label="Mode" value={MODE_LABEL[mode] || mode} testId="brief-row-mode" />
                <BriefRow label="Goal" value={OBIETTIVO_LABEL[f.obiettivo] || f.obiettivo} testId="brief-row-goal" />
                <BriefRow label="Duration" value={durataLabel} testId="brief-row-duration" />
                <BriefRow label="Language" value={language === "auto" ? "Auto" : language} testId="brief-row-language" />
                <BriefRow label="Format" value={f.format_id === "random" ? "Pick for me" : (selectedFmt?.nome || f.format_id)} testId="brief-row-format" />
                {referenceId && <BriefRow label="Reference" value="Active" testId="brief-row-reference" />}
              </div>
              <CtaButton className="w-full justify-center mt-5" onClick={() => generate("script")} disabled={phase === "generating"} data-testid="generate-script-btn">
                {phase === "generating" ? "Writing…" : "Generate script"}
              </CtaButton>
              <p className="text-[11px] text-[#9A9591] mt-3 leading-snug text-center">Hook, beats, CTA and caption — ready in under a minute.</p>
            </aside>
          </div>

          {phase === "generating" && (
            <div className="card-soft mt-5 p-8" data-testid="script-lab-generating">
              <MonoLabel><span className="w-1.5 h-1.5 rounded-full bg-[#96691E] inline-block animate-pulse" />WRITING — UNDER A MINUTE</MonoLabel>
              <div className="mt-6 space-y-3">{[100, 80, 60].map((w, i) => <div key={i} className="h-8 rounded-lg bg-[#F3EFE7] animate-pulse" style={{ width: `${w}%` }} />)}</div>
            </div>
          )}

          {phase === "error" && (
            <div className="card-soft mt-5 p-8 flex items-center gap-4" data-testid="script-lab-error">
              <div className="icon-tile" style={{ background: "var(--amber-bg)" }}><AlertTriangle size={20} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} /></div>
              <div className="flex-1">
                <p className="font-bold text-[14px]">{errMsg || "Something broke on our side — try again."}</p>
              </div>
              <SecondaryButton className="!py-2 !px-4 text-[13px]" onClick={() => lastReq.current && generate(lastReq.current.kind, { mode: lastReq.current.body.mode, fields: lastReq.current.body.fields })} data-testid="script-lab-retry-btn">
                <RefreshCw size={14} strokeWidth={1.8} /> Try again
              </SecondaryButton>
            </div>
          )}

          {tab === "script" && phase === "idle" && out && (
            <div className="mt-5 space-y-4" data-testid="script-output">
              <div className="card-soft p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center rounded-full border border-[#181621] bg-[#181621] text-white px-3 py-1 text-[11px] font-bold">{out.format}</span>
                    <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#54505B] tabular-nums">~{result.request.fields.durata}s</span>
                    <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#54505B]">{LANG_LABEL[result.request.language] || result.request.language}</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <SecondaryButton className="!py-2 !px-3.5 text-[12.5px]" onClick={() => copyText(buildExport(result.request, out))} data-testid="export-copy-btn"><Copy size={13} strokeWidth={1.8} /> Copy all</SecondaryButton>
                    <SecondaryButton className="!py-2 !px-3.5 text-[12.5px]" onClick={() => { const blob = new Blob([buildExport(result.request, out)], { type: "text/plain" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "script.txt"; a.click(); }} data-testid="export-txt-btn"><Download size={13} strokeWidth={1.8} /> .txt</SecondaryButton>
                    <SecondaryButton className="!py-2 !px-3.5 text-[12.5px]" onClick={() => generate("script")} data-testid="regenerate-btn"><RefreshCw size={13} strokeWidth={1.8} /> Regenerate</SecondaryButton>
                    <AddToCalendar added={!!calAdded[result.id]} busy={calBusy} onAdd={addScriptToCalendar} testId="script-add-cal" />
                  </div>
                </div>
                <p className="mt-4 text-[13px] text-[#54505B]"><span className="text-[10px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">Why it works</span>{out.strategia}</p>
                <div className="mt-4 bg-[#FAF8F3] rounded-xl p-4 border-l-4 border-l-[#E0603A] flex items-start justify-between gap-3">
                  <p className="font-bold text-[15px]" data-testid="script-hook">"{out.hook}"</p>
                  <button onClick={() => copyText(out.hook)} className="text-[#9A9591] hover:text-[#181621] shrink-0" data-testid="copy-hook-btn"><Copy size={14} strokeWidth={1.8} /></button>
                </div>
              </div>

              {out.beats.map((b, i) => (
                <div key={i} className="card-soft p-5" data-testid={`beat-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="text-[11px] font-bold tabular-nums bg-[#181621] text-[#FAF8F3] px-2 py-0.5 rounded-md">{b.tempo}</span>
                      <span className="text-[10.5px] font-bold tracking-[0.08em] text-[#54505B] uppercase">{b.label}</span>
                    </div>
                    <button onClick={() => copyText(`${b.voiceover}\n${b.azione}\n${b.onscreen}`)} className="text-[#9A9591] hover:text-[#181621]" data-testid={`copy-beat-${i}`}><Copy size={14} strokeWidth={1.8} /></button>
                  </div>
                  <div className="mt-3 space-y-2 text-[13.5px]">
                    {b.voiceover && <p><span className="text-[9.5px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">Voiceover</span>{b.voiceover}</p>}
                    {b.azione && <p className="text-[#54505B]"><span className="text-[9.5px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">Action</span>{b.azione}</p>}
                    {b.onscreen && <p><span className="text-[9.5px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">On-screen</span><span className="bg-[#FBEED4] px-1.5 py-0.5 rounded">{b.onscreen}</span></p>}
                  </div>
                </div>
              ))}

              <div className="card-soft p-6 space-y-4">
                <div className="flex items-start justify-between gap-3"><p className="text-[14px]"><span className="text-[10px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">CTA</span><span className="font-bold">{out.cta}</span></p><button onClick={() => copyText(out.cta)} className="text-[#9A9591] hover:text-[#181621]" data-testid="copy-cta-btn"><Copy size={14} strokeWidth={1.8} /></button></div>
                <div className="flex items-start justify-between gap-3"><p className="text-[13px] text-[#54505B] whitespace-pre-line"><span className="text-[10px] font-bold tracking-[0.08em] text-[#9A9591] uppercase mr-2">Caption</span>{out.caption}</p><button onClick={() => copyText(out.caption)} className="text-[#9A9591] hover:text-[#181621]" data-testid="copy-caption-btn"><Copy size={14} strokeWidth={1.8} /></button></div>
                <div>
                  <span className="text-[10px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">Direction notes</span>
                  <ul className="mt-2 space-y-1.5">{out.regia.map((n, i) => <li key={i} className="text-[13px] flex gap-2"><span className="w-1 h-1 rounded-full bg-[#54505B] mt-2 shrink-0" />{n}</li>)}</ul>
                </div>
                <div className="flex gap-2 pt-2 border-t border-[#F3EFE7]">
                  <input className="field-input flex-1" placeholder="Refine with instructions — e.g. shorter hook, mention the discount…" value={refine} onChange={(e) => setRefine(e.target.value)} data-testid="refine-input" />
                  <CtaButton className="!py-2.5 !px-4 text-[13px]" disabled={!refine.trim()} onClick={() => { generate("script", { mode: result.request.mode, fields: result.request.fields, refine: { refine_raw: result.raw, refine_instructions: refine } }); setRefine(""); }} data-testid="refine-btn">
                    <Wand2 size={14} strokeWidth={1.8} /> Refine
                  </CtaButton>
                </div>
              </div>
            </div>
          )}

          {tab === "script" && (
            <div className="mt-10" data-testid="history-list">
              <SectionLabel>Recent generations</SectionLabel>
              <div className="mt-3 space-y-2.5">
                {history.length === 0 && <div className="card-soft p-6 text-center text-[13px] text-[#54505B]" data-testid="history-empty">Nothing generated yet.</div>}
                {history.filter((h) => h.kind !== "plan").map((h) => (
                  <button key={h.id} className="card-soft p-4 w-full flex items-center justify-between gap-3 text-left hover:border-[#181621] transition-colors" onClick={() => { setResult(h); window.scrollTo(0, 0); }} data-testid={`history-item-${h.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#BCD4F2] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#3565A8] shrink-0">
                        <Clapperboard size={10} strokeWidth={2} /> Script
                      </span>
                      <span className="font-bold text-[13.5px] truncate">{h.output.hook || h.output.format}</span>
                    </div>
                    <span className="text-[11px] font-semibold text-[#9A9591] tabular-nums shrink-0">{new Date(h.created_at).toLocaleDateString("en-GB")} {new Date(h.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
